import React, { useEffect, useState } from 'react';
import { Plus, Package, Pencil, Trash2 } from 'lucide-react';
import { Card, Btn, Input, Field, Modal, Table, Spinner, ErrBox } from '../../../components/UI';
import { inventoryAPI, masterAPI } from '../../../utils/api';
import { fmtRp } from '../../../utils/format';

const levelBadge = (val, color) => val ? (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600, background:`${color}22`, color, border:`1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

export default function ItemsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refAkun, setRefAkun] = useState([]);
  const [categories] = useState([
    { value: 'BARANG', label: 'Barang' },
    { value: 'BHP', label: 'Bahan Habis Pakai' },
    { value: 'PERALATAN', label: 'Peralatan' },
    { value: 'KOMPONEN', label: 'Komponen' },
    { value: 'BAHAN', label: 'Bahan' },
  ]);
  const [form, setForm] = useState({ kode: '', nama: '', kategori: 'BARANG', satuan: 'UNIT', harga_estimasi: 0, akun_id: '' });

  useEffect(() => { loadItems(); loadRefs(); }, [page]);

  const loadRefs = async () => {
    try {
      const res = await masterAPI.getCoaAkun();
      setRefAkun((res.data||[]).map(x=>({
        value: x.id,
        label: `${x.kode} - ${x.nama}`,
        program_kode: x.program_kode, program_nama: x.program_nama,
        kegiatan_kode: x.kegiatan_kode, kegiatan_nama: x.kegiatan_nama,
        kro_kode: x.kro_kode, kro_nama: x.kro_nama,
      })));
    } catch {}
  };

  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await inventoryAPI.getItems(`?page=${page}&limit=10`);
      setItems(r.data || []);
      setTotal(r.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.kode || !form.nama) return alert('Kode dan Nama barang wajib diisi');
    setSaving(true);
    try {
      const payload = { kode: form.kode, nama: form.nama, kategori: form.kategori, satuan: form.satuan, harga_estimasi: parseInt(form.harga_estimasi) || 0, akun_id: form.akun_id || null };
      if (editId) { await inventoryAPI.updateItem(editId, payload); }
      else { await inventoryAPI.createItem(payload); }
      closeModal();
      loadItems();
    } catch (e) { alert('Gagal menyimpan: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({ kode: item.kode, nama: item.nama, kategori: item.kategori || 'BARANG', satuan: item.satuan || 'UNIT', harga_estimasi: item.harga_estimasi || 0, akun_id: item.akun_id || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus barang ini?')) return;
    try { await inventoryAPI.deleteItem(id); loadItems(); }
    catch (e) { alert('Gagal menghapus: ' + e.message); }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm({ kode: '', nama: '', kategori: 'BARANG', satuan: 'UNIT', harga_estimasi: 0, akun_id: '' });
  };

  const selectedAkun = refAkun.find(x => x.value === form.akun_id);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Btn icon={<Plus size={14}/>} onClick={() => setShowModal(true)}>Tambah Barang</Btn>
      </div>

      <Card>
        {error && <div style={{ marginBottom: 12 }}><ErrBox msg={error} onRetry={loadItems} /></div>}
        {loading ? <Spinner /> : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
            <Package size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>Belum ada barang. Tambahkan barang baru.</p>
          </div>
        ) : (
          <>
            <Table pageSize={10} cols={[
              { key: 'kode', label: 'Kode', nowrap: true },
              { key: 'nama', label: 'Nama Barang' },
              { key: 'kategori', label: 'Kategori', align: 'center' },
              { key: 'program_kode', label: 'Program', render:(v,r)=><ProgramBadge v={v}/> },
              { key: 'kegiatan_kode', label: 'Kegiatan', render:(v,r)=><KegiatanBadge v={v}/> },
              { key: 'kro_kode', label: 'KRO', render:(v,r)=><KroBadge v={v}/> },
              { key: 'akun_kode', label: 'Akun', render:(v,r)=>(
                <div>
                  <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#374151"}}>{v || '-'}</span>
                  <br/><span style={{fontSize:11,color:"#6B7280"}}>{r.akun_nama || '-'}</span>
                </div>
              )},
              { key: 'satuan', label: 'Satuan', align: 'center' },
              { key: 'harga_estimasi', label: 'Harga', align: 'right', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1E40AF' }}>{fmtRp(v)}</span> },
              { key: 'id', label: 'Aksi', render: (id, row) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn size="sm" variant="secondary" icon={<Pencil size={12}/>} onClick={() => handleEdit(row)}>Edit</Btn>
                  <Btn size="sm" variant="danger" icon={<Trash2 size={12}/>} onClick={() => handleDelete(id)}>Hapus</Btn>
                </div>
              )},
            ]} data={items} emptyMsg="Belum ada barang." />
            {total > 10 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <Btn size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Btn>
                <span style={{ padding: '6px 12px', fontSize: 13 }}>Halaman {page} dari {Math.ceil(total / 10)}</span>
                <Btn size="sm" variant="ghost" disabled={page * 10 >= total} onClick={() => setPage(p => p + 1)}>Next</Btn>
              </div>
            )}
          </>
        )}
      </Card>

      {showModal && (
        <Modal title={editId ? 'Edit Barang' : 'Tambah Barang Baru'} onClose={closeModal}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Kode Barang" required>
              <Input value={form.kode} onChange={e => setForm({ ...form, kode: e.target.value })} placeholder="BRG-001" />
            </Field>
            <Field label="Nama Barang" required>
              <Input value={form.nama} onChange={e => setForm({ ...form, nama: e.target.value })} placeholder="Nama barang" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Kategori">
              <select value={form.kategori} onChange={e => setForm({ ...form, kategori: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}>
                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Satuan">
              <Input value={form.satuan} onChange={e => setForm({ ...form, satuan: e.target.value })} placeholder="UNIT" />
            </Field>
          </div>
          <Field label="Harga Estimasi (Rp)">
            <Input type="number" value={form.harga_estimasi} onChange={e => setForm({ ...form, harga_estimasi: e.target.value })} placeholder="0" />
          </Field>
          <Field label="Akun MA">
            <select value={form.akun_id} onChange={e => setForm({ ...form, akun_id: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'white' }}>
              <option value="">Pilih akun...</option>
              {refAkun.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </Field>
          {selectedAkun && (
            <div style={{ marginBottom:16, padding:10, background:"#F8FAFC", borderRadius:8, fontSize:12, display:"flex", gap:6, flexWrap:"wrap" }}>
              <ProgramBadge v={selectedAkun.program_kode ? `${selectedAkun.program_kode} - ${selectedAkun.program_nama}` : null}/>
              <KegiatanBadge v={selectedAkun.kegiatan_kode ? `${selectedAkun.kegiatan_kode} - ${selectedAkun.kegiatan_nama}` : null}/>
              <KroBadge v={selectedAkun.kro_kode ? `${selectedAkun.kro_kode} - ${selectedAkun.kro_nama}` : null}/>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="secondary" onClick={closeModal}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}